// services/usersApi.ts
import { createApi } from '@reduxjs/toolkit/query/react';
import {
  AdminRole,
  CoinsPlan,
  Country,
  CountryDetails,
  DisputeStatus,
  JobType,
  PaymentMethod,
} from '../../../types/lookups';
import { PaginatedCategories, SubCategory, Attribute } from '../../../types/lookups';
import { baseQueryWithReauth } from '../baseQuery';
import { LookupItem } from '../../../types/global';

type ProfileTypes = {
  payload: LookupItem[];
};

export const lookupsService = createApi({
  reducerPath: 'lookupsService',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['OperatorCategories'],
  endpoints: (builder) => ({
    getCountries: builder.query<Country[], void>({
      query: () => ({
        url: `/lookup-service/v1/countries`,
      }),
      transformResponse: (rawResult: { payload: Country[] }) => {
        return rawResult.payload;
      },
    }),
    getCountriesDetails: builder.query<CountryDetails[], void>({
      query: () => ({
        url: `/lookup-service/v1/countries/lookup`,
      }),
      transformResponse: (rawResult: { payload: CountryDetails[] }) => {
        return rawResult.payload;
      },
    }),
    getCountryStates: builder.query<Country[], { countryId: string }>({
      query: ({ countryId }) => ({
        url: `/lookup-service/v1/states?country-id=${countryId}`,
      }),
      transformResponse: (rawResult: { payload: Country[] }) => {
        return rawResult.payload;
      },
    }),
    getProfileTypes: builder.query<ProfileTypes['payload'], void>({
      query: () => ({
        url: '/profile-service/v1/operator/profile-types',
      }),
      transformResponse: (rawResult: ProfileTypes) => {
        return rawResult.payload;
      },
    }),
    getBusinessTypes: builder.query<ProfileTypes['payload'], void>({
      query: () => ({
        url: '/profile-service/v1/operator/profile-types/business',
      }),
      transformResponse: (rawResult: ProfileTypes) => {
        return rawResult.payload;
      },
    }),
    getCompanyTypes: builder.query<ProfileTypes['payload'], void>({
      query: () => ({
        url: '/profile-service/v1/company-types',
      }),
      transformResponse: (rawResult: ProfileTypes) => {
        return rawResult.payload;
      },
    }),
    getCompanySizes: builder.query<ProfileTypes['payload'], void>({
      query: () => ({
        url: '/profile-service/v1/company-size',
      }),
      transformResponse: (rawResult: ProfileTypes) => {
        return rawResult.payload;
      },
    }),
    getJobStatuses: builder.query<JobType[], void>({
      query: () => ({
        url: `/jobs/v1/job-statuses`,
      }),
      transformResponse: (rawResult: { payload: JobType[] }) => {
        return rawResult.payload;
      },
    }),
    getPaymentMethods: builder.query<PaymentMethod[], void>({
      query: () => ({
        url: `/jobs/v1/payment-method`,
      }),
      transformResponse: (rawResult: { payload: PaymentMethod[] }) => {
        return rawResult.payload;
      },
    }),
    getCoinsPlans: builder.query<CoinsPlan[], void>({
      query: () => ({
        url: `/payments-service/v1/coins-plans/all-plans`,
      }),
      transformResponse: (rawResult: { payload: CoinsPlan[] }) => {
        return rawResult.payload;
      },
    }),
    getCoinsPaymentMethods: builder.query<PaymentMethod[], void>({
      query: () => ({
        url: `/payments-service/v1/transaction-method`,
      }),
      transformResponse: (rawResult: { payload: PaymentMethod[] }) => {
        return rawResult.payload;
      },
    }),
    getCommissionPaymentMethods: builder.query<PaymentMethod[], void>({
      query: () => ({
        url: `/payments-service/v1/payment-method`,
      }),
      transformResponse: (rawResult: { payload: PaymentMethod[] }) => {
        return rawResult.payload;
      },
    }),
    getCoinsBudget: builder.query<{ min: number; max: number }, void>({
      query: () => ({
        url: `/payments-service/v1/purchased-coins/budgets-lk`,
      }),
      transformResponse: (rawResult: {
        payload: { min: number; max: number };
      }) => {
        return rawResult.payload;
      },
    }),
    getCommissionBudget: builder.query<{ min: number; max: number }, void>({
      query: () => ({
        url: `/payments-service/v1/commission/budgets-lk`,
      }),
      transformResponse: (rawResult: {
        payload: { min: number; max: number };
      }) => {
        return rawResult.payload;
      },
    }),
    getDisputeStatus: builder.query<DisputeStatus[], void>({
      query: () => ({
        url: `/jobs/v1/job-dispute-status`,
      }),
      transformResponse: (rawResult: { payload: DisputeStatus[] }) => {
        return rawResult.payload;
      },
    }),
    getAdminRoles: builder.query<AdminRole[], void>({
      query: () => ({
        url: `/v1/role/system-roles`,
      }),
      transformResponse: (rawResult: { payload: AdminRole[] }) => {
        return rawResult.payload;
      },
    }),
    getCustomizationTypes: builder.query<LookupItem[], void>({
      query: () => ({
        url: `/lookup-service/v1/customization-types`,
      }),
      transformResponse: (rawResult: { payload: LookupItem[] }) => {
        return rawResult.payload;
      },
    }),
    getOperatorCategories: builder.query<PaginatedCategories, { page: number; search?: string }>({
      query: ({ page, search }) => ({
        url: `/lookup-service/v1/operator/categories`,
        params: {
          page,
          search,
        },
      }),
      providesTags: ['OperatorCategories'],
      transformResponse: (rawResult: { payload: PaginatedCategories } | PaginatedCategories) => {
        return 'payload' in rawResult ? rawResult.payload : rawResult;
      },
    }),
    addOperatorCategory: builder.mutation<any, any>({
      query: (body) => ({
        url: `/lookup-service/v1/operator/categories`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error) => error ? [] : ['OperatorCategories'],
    }),
    updateOperatorCategory: builder.mutation<any, { id: string; body: any }>({
      query: ({ id, body }) => ({
        url: `/lookup-service/v1/operator/categories/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error) => error ? [] : ['OperatorCategories'],
    }),
    deleteOperatorCategory: builder.mutation<any, string>({
      query: (id) => ({
        url: `/lookup-service/v1/operator/categories/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error) => error ? [] : ['OperatorCategories'],
    }),
    updateOperatorSpeciality: builder.mutation<any, { id: string; body: any }>({
      query: ({ id, body }) => ({
        url: `/lookup-service/v1/operator/specialities/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error) => error ? [] : ['OperatorCategories'],
    }),
    deleteOperatorSpeciality: builder.mutation<any, string>({
      query: (id) => ({
        url: `/lookup-service/v1/operator/specialities/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error) => error ? [] : ['OperatorCategories'],
    }),
    addOperatorSpeciality: builder.mutation<any, any>({
      query: (body) => ({
        url: `/lookup-service/v1/operator/specialities`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error) => error ? [] : ['OperatorCategories'],
    }),
    uploadOperatorCategoryDocument: builder.mutation<any, FormData>({
      query: (body) => ({
        url: `/lookup-service/v1/operator/categories/documents`,
        method: 'POST',
        body,
      }),
    }),
    uploadOperatorSpecialityDocument: builder.mutation<any, FormData>({
      query: (body) => ({
        url: `/lookup-service/v1/operator/specialities/documents`,
        method: 'POST',
        body,
      }),
    }),
    getOperatorCategorySpecialities: builder.query<SubCategory[], { categoryId: string }>({
      query: ({ categoryId }) => ({
        url: `/lookup-service/v1/operator/category/${categoryId}/specialities`,
      }),
      transformResponse: (rawResult: { payload: SubCategory[] } | SubCategory[]) => {
        return 'payload' in rawResult ? rawResult.payload : rawResult;
      },
    }),
    getOperatorAttributes: builder.query<Attribute[], void>({
      query: () => ({
        url: `/lookup-service/v1/operator/attributes`,
      }),
      transformResponse: (rawResult: { payload: Attribute[] } | Attribute[]) => {
        return 'payload' in rawResult ? rawResult.payload : rawResult;
      },
    }),
    getCategoryAttributes: builder.query<any, { categoryId: string }>({
      query: ({ categoryId }) => ({
        url: `/lookup-service/v1/categories/${categoryId}/attributes`,
      }),
    }),

    getIndustries: builder.query<any, void>({
      query: () => ({
        url: `/lookup-service/v1/categories`,
      }),
      transformResponse: (rawResult: { payload: any }) => {
        return rawResult.payload;
      },
    }),
    getIndustrySpecialities: builder.query<any, { categoryId: string }>({
      query: ({ categoryId }) => ({
        url: `/lookup-service/v1/specialities`,
        params: {
          'category-id': categoryId,
        },
      }),
      transformResponse: (rawResult: { payload: any }) => {
        return rawResult.payload;
      },
    }),
    getHsCodes: builder.query<any, { search: string }>({
      query: ({ search }) => ({
        url: `/lookup-service/v1/hs-codes`,
        params: {
          search,
        },
      }),
      transformResponse: (rawResult: { payload: any }) => {
        return rawResult.payload;
      },
    }),
    getCurrenciesPreference: builder.query<any, void>({
      query: () => ({
        url: `/lookup-service/v1/currencies`,
      }),
      transformResponse: (rawResult: { payload: any }) => {
        return rawResult.payload;
      },
    }),
    getSupportTicketStatuses: builder.query<any, void>({
      query: () => ({
        url: `/lookup-service/v1/support-ticket-status`,
      }),
      transformResponse: (rawResult: { payload: any }) => {
        return rawResult.payload;
      },
    }),
  }),
});

export const {
  useGetCountriesQuery,
  useGetCountriesDetailsQuery,
  useGetCountryStatesQuery,
  useGetCompanyTypesQuery,
  useGetCompanySizesQuery,
  useGetProfileTypesQuery,
  useGetBusinessTypesQuery,
  useGetJobStatusesQuery,
  useGetPaymentMethodsQuery,
  useGetCoinsPlansQuery,
  useGetCoinsPaymentMethodsQuery,
  useGetCommissionPaymentMethodsQuery,
  useGetCoinsBudgetQuery,
  useGetCommissionBudgetQuery,
  useGetDisputeStatusQuery,
  useGetAdminRolesQuery,
  useGetCustomizationTypesQuery,
  useGetOperatorCategoriesQuery,
  useAddOperatorCategoryMutation,
  useUpdateOperatorCategoryMutation,
  useDeleteOperatorCategoryMutation,
  useUpdateOperatorSpecialityMutation,
  useDeleteOperatorSpecialityMutation,
  useAddOperatorSpecialityMutation,
  useUploadOperatorCategoryDocumentMutation,
  useUploadOperatorSpecialityDocumentMutation,
  useLazyGetOperatorCategorySpecialitiesQuery,
  useGetOperatorAttributesQuery,
  useGetCategoryAttributesQuery,
  useLazyGetCategoryAttributesQuery,

  useGetIndustriesQuery,
  useGetIndustrySpecialitiesQuery,
  useGetHsCodesQuery,
  useGetCurrenciesPreferenceQuery,
  useGetSupportTicketStatusesQuery,
} = lookupsService;
